import React, { useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, HeadphonesIcon, SparklesIcon, CheckIcon, PlayIcon, PauseIcon, ClockIcon } from 'lucide-react';
import { GlowButton } from './GlowButton';
import { useLanguage } from '../contexts/LanguageContext';
const AUDIO_PREVIEW_URL = 'https://github.com/jvzng/webpage_chatbot_closer/raw/980fc46fbc1f0174fb4817a62896dc782237fcd9/ElevenLabs_2026-02-19T20_15_33_Declan%20Sage%20-%20Wise%2C%20Deliberate%2C%20Captivating_pvc_sp76_s50_sb50_se48_b_m2.mp3';
function AudioPreview() {
  const {
    t,
    audioUrl
  } = useLanguage();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const current = audioRef.current.currentTime;
    const dur = audioRef.current.duration;
    setCurrentTime(current);
    if (dur) setProgress(current / dur * 100);
  };
  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * duration;
  };
  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };
  const formatTime = (t: number) => {
    const mins = Math.floor(t / 60);
    const secs = Math.floor(t % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="rounded-xl bg-white/[0.04] border border-purple-electric/15 p-4 mb-6" data-id="element-234">
      <audio ref={audioRef} src={audioUrl} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} preload="metadata" data-id="element-235" />
      <p className="font-sans text-[11px] text-purple-electric-light/60 tracking-wider uppercase mb-3" data-id="element-236"></p>
      <div className="flex items-center gap-3" data-id="element-237">
        <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-purple-electric/20 border border-purple-electric/30 flex items-center justify-center flex-shrink-0 hover:bg-purple-electric/30 transition-colors duration-300" data-id="element-238">
          {isPlaying ? <PauseIcon className="w-4 h-4 text-purple-electric-light" data-id="element-239" /> : <PlayIcon className="w-4 h-4 text-purple-electric-light ml-0.5" data-id="element-240" />}
        </button>
        <div className="flex-1 min-w-0" data-id="element-241">
          <div className="w-full h-1.5 bg-white/[0.06] rounded-full cursor-pointer group" onClick={handleSeek} data-id="element-242">
            <div className="h-full bg-gradient-to-r from-purple-electric to-purple-electric-light rounded-full transition-all duration-100 relative" style={{
            width: `${progress}%`
          }} data-id="element-243">
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-purple-electric-light rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-glow-purple" data-id="element-244" />
            </div>
          </div>
          <div className="flex justify-between mt-1.5" data-id="element-245">
            <span className="font-sans text-[10px] text-white/25" data-id="element-246">
              {formatTime(currentTime)}
            </span>
            <span className="font-sans text-[10px] text-white/25" data-id="element-247">
              {duration ? formatTime(duration) : '--:--'}
            </span>
          </div>
        </div>
      </div>
    </div>;
}
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function FeatureItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-3" data-id="element-248">
      <CheckIcon className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" data-id="element-249" />
      <span className="font-sans text-white/50 text-sm leading-relaxed" data-id="element-250">
        {children}
      </span>
    </li>;
}
export function ProductSection() {
  const {
    t
  } = useLanguage();
  return <section id="producto" className="relative w-full py-8 sm:py-14 overflow-hidden" data-id="element-251">
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-flame/[0.04] rounded-full blur-[120px] pointer-events-none" data-id="element-252" />
      <div className="absolute bottom-0 right-0 w-[350px] h-[350px] bg-purple-electric/[0.04] rounded-full blur-[100px] pointer-events-none" data-id="element-253" />

      <div className="relative max-w-6xl mx-auto px-6" data-id="element-254">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-6 sm:mb-10" data-id="element-255">
          <p className="font-sans text-sm text-gold tracking-[0.2em] uppercase mb-4" data-id="element-256">
            {t('product.eyebrow')}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-257">
            {t('product.title')}
          </h2>
          <p className="font-sans text-white/40 text-lg max-w-2xl mx-auto" data-id="element-258">
            {t('product.subtitle')}
          </p>
          {/* Bonus badge */}
          <div className="flex justify-center mt-4" data-id="element-259">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/15 border border-teal/30 text-white/70 text-xs font-sans font-medium tracking-wide" data-id="element-260">
              {t('hero.bonusBadge')}
            </span>
          </div>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-261">
          {/* Ebook Card */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-262">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-flame/15 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-263" />
            <div className="relative rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] p-6 sm:p-8 h-full overflow-hidden" data-id="element-264">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-flame/50 to-transparent" data-id="element-265" />
              <div className="absolute top-4 right-4" data-id="element-266">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/10 border border-gold/20 text-gold-light text-xs font-sans font-medium" data-id="element-267">
                  <CheckIcon className="w-3 h-3" data-id="element-268" />
                  {t('product.included')}
                </span>
              </div>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-flame/20 to-flame/5 border border-flame/20 flex items-center justify-center mb-6" data-id="element-269">
                <BookOpenIcon className="w-8 h-8 text-flame-light" data-id="element-270" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2" data-id="element-271">
                {t('product.ebook')}
              </h3>
              <p className="font-sans text-flame-light/70 text-sm tracking-wide uppercase mb-6" data-id="element-272">
                {t('product.ebookFormat')}
              </p>
              <ul className="space-y-3" data-id="element-273">
                <FeatureItem data-id="element-274">{t('product.ebook.1')}</FeatureItem>
                <FeatureItem data-id="element-275">{t('product.ebook.2')}</FeatureItem>
                <FeatureItem data-id="element-276">{t('product.ebook.3')}</FeatureItem>
                <FeatureItem data-id="element-277">{t('product.ebook.4')}</FeatureItem>
              </ul>
            </div>
          </motion.div>

          {/* Audiobook Card */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-278">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-electric/15 to-astral/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-279" />
            <div className="relative rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] p-6 sm:p-8 h-full overflow-hidden" data-id="element-280">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-electric/50 to-transparent" data-id="element-281" />
              <div className="absolute top-4 right-4" data-id="element-282">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-electric/15 border border-purple-electric/25 text-purple-electric-light text-xs font-sans font-medium" data-id="element-283">
                  <CheckIcon className="w-3 h-3" data-id="element-284" />
                  {t('product.included')}
                </span>
              </div>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-electric/20 to-purple-electric/5 border border-purple-electric/20 flex items-center justify-center mb-6" data-id="element-285">
                <HeadphonesIcon className="w-8 h-8 text-purple-electric-light" data-id="element-286" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2" data-id="element-287">
                {t('product.audiobook')}
              </h3>
              <p className="font-sans text-purple-electric-light/70 text-sm tracking-wide uppercase mb-6" data-id="element-288">
                {t('product.audiobookFormat')}
              </p>
              <div className="mb-3" data-id="element-289">
                <p className="font-sans text-xs text-white/80 font-medium leading-snug mb-0.5" data-id="element-290">
                  {t('hero.audioWarning')}
                </p>
                <p className="font-sans text-[11px] leading-snug text-[#FCFCFC]" data-id="element-291">
                  {t('hero.audioDescription')}
                </p>
              </div>
              <AudioPreview data-id="element-292" />
              <ul className="space-y-3 mb-4" data-id="element-293">
                <FeatureItem data-id="element-294">{t('product.audiobook.1')}</FeatureItem>
                <FeatureItem data-id="element-295">{t('product.audiobook.2')}</FeatureItem>
                <FeatureItem data-id="element-296">{t('product.audiobook.3')}</FeatureItem>
                <FeatureItem data-id="element-297">{t('product.audiobook.4')}</FeatureItem>
              </ul>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] w-fit" data-id="element-298">
                <ClockIcon className="w-3.5 h-3.5 text-purple-electric-light/60" data-id="element-299" />
                <span className="font-sans text-xs text-white/40" data-id="element-300">
                  {t('product.duration')}{' '}
                  <span className="text-purple-electric-light/80 font-medium" data-id="element-301">
                    {t('product.hours')}
                  </span>
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex flex-col items-center mt-6 sm:mt-10 gap-4" data-id="element-302">
          {/* Bonus badge */}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/15 border border-teal/30 text-white/70 font-sans font-medium tracking-wide text-[20px]" data-id="element-303">
            {t('hero.bonusBadge')}
          </span>
          <GlowButton href="#obtener-pack" variant="gold" data-id="element-304">
            {t('hero.cta')}
          </GlowButton>
          <p className="font-sans text-[11px] text-white/30 tracking-wide text-center" data-id="element-305">
            {t('hero.trustLine')}
          </p>
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/[0.03] backdrop-blur-sm border border-gold/15" data-id="element-306">
            <SparklesIcon className="w-4 h-4 text-gold" data-id="element-307" />
            <span className="font-sans text-white/50 text-sm" data-id="element-308">
              {t('product.bundle')}
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}